import { useMemo } from 'react';
import { IPickerProps } from '../../types';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Spinner from 'src/view/components/Controls/Spinner/Spinner';
import '../../style.scss';

type TPickerInput<T> = Pick<
  IPickerProps<T>,
  | 'disabled'
  | 'loading'
  | 'noReset'
  | 'hideResetIcon'
  | 'selectedOptions'
  | 'options'
  | 'propsOptions'
  | 'validation'
  | 'placeholder'
  | 'viewedType'
  | 'viewedSize'
  | 'inputClassName'
  | 'defaultPlaceholderClassName'
  | 'selectedItemsPlaceholder'
  | 'notUsePlaceholderOnAllSelected'
>;

interface IProps<T> extends TPickerInput<T> {
  isOpen?: boolean;
  loading?: boolean;
  emptyPlaceHolder?: string;
  toggleOpen: () => void;
  clickReset: () => void;
}

const PickerInput = <T,>(props: IProps<T>): JSX.Element => {
  const isSmallSize = useMemo((): boolean => {
    return props.viewedSize === 'small';
  }, [props.viewedSize]);

  const resetButton = useMemo((): boolean => {
    return Boolean(!props.noReset && !props.hideResetIcon && props.selectedOptions?.length);
  }, [props.noReset, props.hideResetIcon, props.selectedOptions]);

  const placeholder = useMemo((): string => {
    if (!props.selectedOptions?.length) return localeStore.t(props.emptyPlaceHolder || props.placeholder || 'select_item');
    if (props.selectedOptions?.length === 1) return localeStore.t(props.selectedOptions[0].label);
    const optionsLength = props.options?.length || 0;
    const propsOptionsLength = props.propsOptions?.length || 0;
    const selectedOptionsLength = props.selectedOptions?.length || 0;
    const allSelected = propsOptionsLength >= optionsLength ? propsOptionsLength === selectedOptionsLength : selectedOptionsLength === optionsLength;

    if ((props.options?.length || props.propsOptions?.length) && allSelected && !props.notUsePlaceholderOnAllSelected) {
      return localeStore.t(props.placeholder || 'select_item');
    }
    if (props.selectedOptions?.length && props.selectedOptions.length > 1)
      return `${localeStore.t(props.selectedItemsPlaceholder ?? 'selected')} (${props.selectedOptions.length})`;

    return '';
  }, [
    props.selectedOptions,
    props.placeholder,
    props.options?.length,
    props.propsOptions?.length,
    props.emptyPlaceHolder,
    props.selectedItemsPlaceholder,
  ]);

  const className = useMemo((): string => {
    let _className: string = 'custom-picker__open-input';

    if (props.validation && !props.validation.isValid) _className += ' input-error';
    if (props.inputClassName) _className += ` ${props.inputClassName}`;
    if (props.disabled) _className += ' cursor-not-allowed';

    return _className;
  }, [props.inputClassName, props.validation, props.disabled]);

  const placeholderWrapperClassName = useMemo((): string => {
    let _className: string = 'custom-picker__placeholder-wrapper';

    if (resetButton) _className += ' custom-picker__placeholder-wrapper-with-reset';
    _className += isSmallSize ? ' pl-3' : ' pl-4';

    return _className;
  }, [resetButton, isSmallSize]);

  const placeholderClassName = useMemo((): string => {
    let _className: string = 'custom-picker__placeholder';

    if (!props.selectedOptions?.length && props.defaultPlaceholderClassName) _className += ` ${props.defaultPlaceholderClassName}`;

    return _className;
  }, [props.selectedOptions, props.defaultPlaceholderClassName]);

  if (props.viewedType === 'input') {
    return (
      <div className={className}>
        {/* placeholder */}
        <div className={placeholderWrapperClassName} onClick={props.toggleOpen}>
          <p className={placeholderClassName}>{placeholder}</p>
        </div>

        <div className="d-flex flex-row full-height align-items-center">
          {/* reset button */}
          {resetButton && !props.loading ? (
            <button className={`custom-picker__icon full-height p-1 ${props.isOpen ? 'custom-picker__icon-reset' : ''}`} onClick={props.clickReset}>
              <Icon name={Icons.CloseSmall} size={16} color={theme.colors.gray2} />
            </button>
          ) : null}
          {/* loading spinner */}
          <div className="d-flex full-height align-items-center" onClick={props.toggleOpen}>
            {props.loading ? (
              <Spinner color={theme.colors.gray} size={isSmallSize ? 20 : 24} className="pr-2" />
            ) : (
              <Icon name={Icons.Chevron} size={20} color={theme.colors.gray2} className="custom-picker__icon" />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-picker__open-icon">
      <button className="custom-picker__icon-wrapper p-0 ml-3" onClick={props.toggleOpen}>
        <Icon name={Icons.Chevron} size={18} color={theme.colors.dark} className="mx-auto" />
      </button>
      {resetButton ? (
        <button className={`custom-picker__icon p-1 ml-3 ${props.isOpen ? 'custom-picker__icon-reset' : ''}`} onClick={props.clickReset}>
          <Icon name={Icons.CloseSmall} size={14} color={theme.colors.dark} />
        </button>
      ) : null}
    </div>
  );
};

export default PickerInput;
